import { useEffect } from "react";
import { useBranding } from "../branding";
import { Logo } from "../components";
import { useAuthenticatedSession } from "../session";
import { accentOverride } from "../theme/brand";
import { WalletProvider } from "../wallet";
import { AppLayout } from "./AppLayout";
import styles from "./AppLayout.module.css";
import { NAV_ENTREPRISE, NAV_FEUILLES, NAV_PARAMETRES, ROUTES } from "./routes";
import type { NavItem } from "./routes";

/**
 * L'espace d'une entreprise cliente : ses six écrans sur son portefeuille,
 * ses paramètres pour ses administrateurs, et sa marque.
 *
 * L'accent est posé sur la coquille, donc tout ce qui est dessous le suit —
 * navigation, pages, boîtes de dialogue. Les toasts et l'écran de connexion
 * sont hors de la coquille : ils gardent le bleu du site, et la déconnexion
 * démonte la coquille, donc rétablit le thème par défaut sans code.
 */
export function EntrepriseLayout() {
  const { user, company, permissions } = useAuthenticatedSession();
  const { product_short, product_name } = useBranding();

  useEffect(() => {
    const avant = document.title;
    document.title = company ? `${company.name} · ${product_short}` : `${product_short} · ${product_name}`;
    return () => {
      document.title = avant;
    };
  }, [company, product_short, product_name]);

  if (company === null) {
    // Un rôle d'entreprise a toujours une entreprise ; la garde de rôle au-dessus
    // ne laisse passer que ceux-là. Ne rien rendre plutôt que de rendre faux.
    return null;
  }

  const items: NavItem[] = NAV_ENTREPRISE.filter(
    (item) => item.label !== "Acheter des tokens" || permissions.can_purchase,
  );
  // Les feuilles de temps, pour qui les valide : les administrateurs et les
  // membres ajoutés aux destinataires. Le droit vient du serveur, pas du rôle.
  if (permissions.can_review_timesheets) {
    items.push(NAV_FEUILLES);
  }
  if (permissions.can_manage_members) {
    items.push(NAV_PARAMETRES);
  }

  return (
    <WalletProvider
      walletId={company.wallet_id}
      companyId={company.id}
      permissions={permissions}
      links={{ commande: ROUTES.commande, achat: permissions.can_purchase ? ROUTES.achat : null }}
    >
      <AppLayout
        navItems={items}
        style={accentOverride(company.accent_color)}
        brand={
          <>
            <Logo src={company.logo_url} name={company.name} />
            <span className={styles.nomProduit}>
              <strong>{company.name}</strong>
              <span>
                {product_short} · {user.role === "admin" ? "administration" : "espace membre"}
              </span>
            </span>
          </>
        }
      />
    </WalletProvider>
  );
}
